// Step6CTA.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Tooltip,
  Slide,
} from '@mui/material';
import {
  ColorLens as ColorIcon,
  TextFields as TextIcon,
  Link as LinkIcon,
} from '@mui/icons-material';

function Step6CTA({ cta, onChange }) {
  const [hasCTA, setHasCTA] = useState(cta?.hasCTA ?? false);
  const [ctaText, setCtaText] = useState(cta?.text || '');
  const [ctaUrl, setCtaUrl] = useState(cta?.url || '');
  const [ctaButtonColor, setCtaButtonColor] = useState(cta?.button_color || '#4CAF50');
  const [ctaTextColor, setCtaTextColor] = useState(cta?.text_color || '#FFFFFF');
  const [error, setError] = useState('');
  const [urlError, setUrlError] = useState('');

  const validateUrl = (url) => {
    if (!url) return true; // Empty is valid (but will be caught if CTA is enabled)
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    validateAndUpdate();
  }, [hasCTA, ctaText, ctaUrl, ctaButtonColor, ctaTextColor]);

  const validateAndUpdate = () => {
    let hasError = false;
    
    if (hasCTA) {
      if (!ctaText.trim()) {
        setError('Please enter text for your call-to-action button');
        hasError = true;
      }
      
      if (!ctaUrl.trim()) {
        setUrlError('Please enter a URL for your call-to-action button');
        hasError = true;
      } else if (!validateUrl(ctaUrl)) {
        setUrlError('Please enter a valid URL (e.g., https://example.com)');
        hasError = true;
      }
    }

    if (!hasError) {
      setError('');
      setUrlError('');
    }

    onChange({
      hasCTA,
      text: hasCTA ? ctaText : null,
      url: hasCTA ? ctaUrl : null,
      button_color: hasCTA ? ctaButtonColor : null,
      text_color: hasCTA ? ctaTextColor : null,
      isValid: !hasCTA || (hasCTA && ctaText.trim().length > 0 && ctaUrl.trim().length > 0 && validateUrl(ctaUrl))
    });
  };

  const handleCtaTextChange = (e) => {
    setCtaText(e.target.value);
  };

  const presetColors = [
    { name: 'Green', button: '#4CAF50', text: '#FFFFFF' },
    { name: 'Blue', button: '#2196F3', text: '#FFFFFF' },
    { name: 'Orange', button: '#FF9800', text: '#000000' },
    { name: 'Purple', button: '#9C27B0', text: '#FFFFFF' },
    { name: 'Red', button: '#F44336', text: '#FFFFFF' },
    { name: 'Dark', button: '#212121', text: '#FFFFFF' },
  ];

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Step 6: Call-to-Action Setup
      </Typography>

      <Box sx={{ mb: 3 }}>
        <RadioGroup 
          value={hasCTA.toString()} 
          onChange={(e) => setHasCTA(e.target.value === 'true')}
        >
          <Box className="radio-options">
            <Paper className="radio-label">
              <FormControlLabel
                value="true"
                control={<Radio />}
                label={<span>Yes, add a call-to-action button to my posts</span>}
              />
              <Typography className="option-description">
                Add a customizable button at the end of each blog post
              </Typography>
            </Paper>

            <Paper className="radio-label">
              <FormControlLabel
                value="false"
                control={<Radio />}
                label={<span>No, skip this step</span>}
              />
              <Typography className="option-description">
                Continue without adding a call-to-action button
              </Typography>
            </Paper>
          </Box>
        </RadioGroup>
      </Box>

      {hasCTA && (
        <Slide direction="up" in={hasCTA} mountOnEnter unmountOnExit>
          <Box>
            <Grid container spacing={3}>
              {/* Text Input */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <TextIcon color="primary" />
                    <Typography variant="subtitle1">Button Text</Typography>
                  </Box>
                  <TextField
                    fullWidth
                    placeholder="e.g., Subscribe Now, Learn More, Get Started"
                    value={ctaText}
                    onChange={handleCtaTextChange}
                    variant="outlined"
                    size="medium"
                    error={!!error}
                    helperText={error}
                    required
                  />
                </Paper>
              </Grid>

              {/* URL Input */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <LinkIcon color="primary" />
                    <Typography variant="subtitle1">Button URL</Typography>
                  </Box>
                  <TextField
                    fullWidth
                    placeholder="e.g., https://example.com/subscribe"
                    value={ctaUrl}
                    onChange={(e) => setCtaUrl(e.target.value)}
                    variant="outlined"
                    size="medium"
                    error={!!urlError}
                    helperText={urlError || "Enter the URL where users will be directed when clicking the button"}
                    required
                  />
                </Paper>
              </Grid>

              {/* Color Selection */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <ColorIcon color="primary" />
                    <Typography variant="subtitle1">Quick Colors</Typography>
                  </Box>
                  <Grid container spacing={1}>
                    {presetColors.map(({ name, button, text }) => (
                      <Grid item key={name}>
                        <Tooltip title={name}>
                          <Box
                            onClick={() => {
                              setCtaButtonColor(button);
                              setCtaTextColor(text);
                            }}
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: button,
                              borderRadius: 1,
                              cursor: 'pointer',
                              border: theme => 
                                button === ctaButtonColor 
                                  ? `2px solid ${theme.palette.primary.main}` 
                                  : '2px solid transparent',
                              '&:hover': { transform: 'scale(1.1)' },
                            }}
                          />
                        </Tooltip>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>

              {/* Custom Colors */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Custom Colors
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" gutterBottom>
                        Button Color
                      </Typography>
                      <input
                        type="color"
                        value={ctaButtonColor}
                        onChange={(e) => setCtaButtonColor(e.target.value)}
                        style={{ width: '100%', height: 40 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" gutterBottom>
                        Text Color
                      </Typography>
                      <input
                        type="color"
                        value={ctaTextColor}
                        onChange={(e) => setCtaTextColor(e.target.value)}
                        style={{ width: '100%', height: 40 }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Preview */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Preview
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <button
                      style={{
                        backgroundColor: ctaButtonColor,
                        color: ctaTextColor,
                        padding: '12px 24px',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: 500,
                      }}
                    >
                      {ctaText || 'Your Call-to-Action'}
                    </button>
                    {ctaUrl && (
                      <Typography variant="caption" color="text.secondary">
                        Links to: {ctaUrl}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Slide>
      )}
    </Box>
  );
}

export default Step6CTA;
